import {WriteButton} from '../../common/buttons/WriteActions';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    FormControl,
    MenuItem,
    Select,
    Stack,
    TableCell,
    TableRow,
    Typography
    ,Alert
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useParams} from "react-router";
import {fillingAPI} from "../../../services/FillingServices";
import {useEffect, useState} from "react";
import {ArrowBackIosNew} from "@mui/icons-material";
import {RECIPE_SIZE} from "@mybake/ui/common/dictionary";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {FillingGuidDto} from "../../../models/Recipe/Filling/IFillingGuid";
import {DeleteFillingDialog} from "./DeleteFillingDialog";
import {getIngredientAmount, getIngredientsTotalWeight} from "../shared/ingredientAmount";
import {AmountValue} from "../shared/AmountValue";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {useCurrency} from '../../../hooks/useCurrency';
import {getApiErrorMessage, getApiTraceId} from '../../../common/apiErrors';
import {ContentSkeleton} from '../../common/loading/ContentSkeleton';

export const FillingDetailCard = () => {
    const {formatCurrency} = useCurrency();
    const navigate = useNavigate();
    const {id} = useParams();
    const [fillingForm, setFillingForm] = useState<FillingGuidDto | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [deleteTraceId, setDeleteTraceId] = useState('');
    const {data: filling, error, isLoading, isSuccess} = fillingAPI.useGetFillingV2Query(id ?? '', {skip: !id})
    const [deleteFilling] = fillingAPI.useDeleteFillingV2Mutation();
    const [size, setSize] = useState("1.00");
    const {t} = useAppTranslation();
    const totalWeight = getIngredientsTotalWeight(fillingForm?.items ?? [], size);
    const handleRecipeSize = (e: any, counter: any) => {
        setSize(e.target.value);
    };

    const handleDelete = () => {
        setDeleteError('');
        setDeleteDialogOpen(!deleteDialogOpen);
    }

    const handleDeleteFilling = async () => {
        if (!fillingForm) return false;

        try {
            await deleteFilling(fillingForm.id).unwrap();
            navigate("..", {relative: "path"});
            return true;
        } catch (requestError) {
            const requestTraceId = getApiTraceId(requestError);
            setDeleteError(getApiErrorMessage(requestError, t));
            setDeleteTraceId(requestTraceId);
            return false;
        }
    };

    useEffect(() => {
        if (isSuccess && filling) {
            setFillingForm(filling);
        }
    }, [isSuccess, filling]);

    return (<Container maxWidth="xl" sx={{py: 4,}}>
        {isLoading && <ContentSkeleton variant="details"/>}
        {error && <Alert severity="error" sx={{mb: 2}}>{getApiErrorMessage(error, t)}</Alert>}
        <Button variant="outlined" startIcon={<ArrowBackIosNew/>}
                onClick={() => navigate("..", {relative: "path"})}>
            Back to fillings
        </Button>
        {!isLoading && <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Stack spacing={1}>
                        <Stack direction="row"
                               spacing={2}
                               justifyContent="space-between"
                               alignItems="center">
                            <Box>
                                <Typography variant="h5">
                                    {fillingForm?.fillingName}
                                </Typography>
                            </Box>
                            <Box paddingRight={2}>
                                <Stack direction="row"
                                       spacing={2}
                                       justifyContent="space-between"
                                       alignItems="center">
                                    <Typography variant="subtitle1">
                                         {t('recipes:proportion')}
                                    </Typography>
                                    <FormControl
                                        variant="outlined"
                                        size="medium"
                                    >
                                        <Select
                                            labelId="recipe_size_label"
                                            id="create_recipe_select"
                                            value={size}
                                            onChange={handleRecipeSize}
                                        >
                                            {RECIPE_SIZE.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </Box>
                        </Stack>
                        <Divider/>

                        <Stack direction={{xs: "column", md: "row",}} spacing={1}>
                            <Box flex={1}>
                                <Typography variant="caption">
                                    Description
                                </Typography>

                                <Typography variant="body1" sx={{mt: 1,}}>
                                    {fillingForm?.fillingDescription || "No description"}
                                </Typography>
                            </Box>

                            <Box minWidth={200}>
                                <Typography variant="caption">
                                    Price
                                </Typography>

                                <Typography variant="h5" sx={{mt: 1,}}>
                                    {
                                        formatCurrency(Number((fillingForm?.price || 0) * Number(size)))
                                    }{" "}
                                    $
                                </Typography>
                                <Typography variant="caption" sx={{display: 'block', mt: 1}}>{t('items:weight')}</Typography>
                                <Typography variant="h5" sx={{mt: 1}}>{totalWeight.toFixed(2)} {t('items:grams_short')}</Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* ITEMS */}

            <Card>
                <CardContent>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            Items
                        </Typography>

                        <Divider/>

                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableBody>
                                    {fillingForm?.items.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>
                                                    {item.itemName}
                                                </TableCell>

                                                <TableCell align="right">
                                                    <AmountValue quantity={getIngredientAmount(item, size).quantity} unit={item.unitOfWeight}/>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </CardContent>
            </Card>

            {/* ACTIONS */}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <WriteButton variant="outlined" startIcon={<EditIcon/>}
                        onClick={() => navigate('edit')}
                >
                    Edit
                </WriteButton>

                <WriteButton
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon/>}
                    onClick={handleDelete}
                >
                    Delete
                </WriteButton>
            </Stack>
        </Stack>}
        {deleteDialogOpen && <DeleteFillingDialog open={deleteDialogOpen} onClose={handleDelete}
                                                  fillingName={fillingForm?.fillingName || ''}
                                                  error={deleteError}
                                                  traceId={deleteTraceId}
                                                  onDelete={handleDeleteFilling}/>}
    </Container>)
}
